/**
 * The "Versioning" dialog's writes — the client side of
 * `reorder_document_versions` and `detach_document_version`
 * (franklin-data-pipeline 20261027105848).
 *
 * As with `add_document_as_version`, the client never writes `version`,
 * `version_group` or `is_latest_version` itself: the RPCs own the lineage
 * lock, the renumbering and the re-election of each lineage's head.
 */
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { isMissingFunctionError } from "@/utils/postgrestErrors";
import {
  pinnedDocumentFromError,
  type PinnedDocument,
} from "@/domains/documents/services/pinnedDocuments";

/** Why a call failed, one per RPC error token plus the client-side cases. */
export type VersionLineageFailureReason =
  | "unavailable"
  | "versionsChanged"
  | "forbidden"
  | "notFound"
  | "notVersionable"
  | "lastVersion"
  | "headInOtherProject"
  | "pinned"
  | "unknown";

export type VersionLineageOutcome<T> =
  | { ok: true; result: T }
  | {
      ok: false;
      reason: VersionLineageFailureReason;
      message: string;
      /** Set for `pinned`: the file that may not leave its folder. */
      pinned?: PinnedDocument;
      /** No verdict from the database (transport failure, unreadable reply):
       *  the call may have committed. A database error rolled back. */
      indeterminate?: boolean;
    };

/** Narrowing helper: the app's tsconfig is not strict, so `!outcome.ok` does
 *  not discriminate the union on its own. */
export function isVersionLineageFailure<T>(
  outcome: VersionLineageOutcome<T>
): outcome is Extract<VersionLineageOutcome<T>, { ok: false }> {
  return outcome.ok === false;
}

export interface ReorderVersionsResult {
  versionGroup: string;
  headDocumentId: number;
  headVersion: number;
  /** True when the new head was moved into the folder the document was shown in. */
  headRelocated: boolean;
  /** Set when the moved head met another document's name and took a " (n)" suffix. */
  headRenamed: { from: string; to: string } | null;
}

export interface DetachVersionResult {
  documentId: number;
  versionGroup: string;
  filename: string;
  /** Where it now lives; undefined when the reply did not say. */
  folderId?: number | null;
  /** Set when the name clashed in its folder and took a " (n)" suffix. */
  renamed: { from: string; to: string } | null;
  sourceVersionGroup: string;
  /** Set when a detached head's predecessor, moving into its folder, met a
   *  clashing name there and took a " (n)" suffix. */
  sourceHeadRenamed: { from: string; to: string } | null;
}

/** The RPCs raise these tokens as the exception message. */
const TOKEN_REASONS: Record<string, VersionLineageFailureReason> = {
  versions_changed: "versionsChanged",
  forbidden: "forbidden",
  lineage_not_found: "notFound",
  document_not_found: "notFound",
  not_versionable: "notVersionable",
  last_version: "lastVersion",
  head_in_other_project: "headInOtherProject",
};

// Once the RPCs are known to be missing (app deployed ahead of the
// migration), the dialog stops offering reorder/detach for the session.
let unavailable = false;

/** False once this session has learned the database has no such RPCs. */
export function isVersionLineageEditAvailable(): boolean {
  return !unavailable;
}

/** Test seam: forget what this session learned. */
export function resetVersionLineageEditAvailability(): void {
  unavailable = false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function failure<T>(
  rpc: string,
  error: { code?: string | null; message?: string },
  extra: Record<string, unknown>
): VersionLineageOutcome<T> {
  const message = error.message ?? "";
  const pinned = pinnedDocumentFromError(error);
  if (pinned) return { ok: false, reason: "pinned", message, pinned };
  if (isMissingFunctionError(error)) {
    unavailable = true;
    return { ok: false, reason: "unavailable", message };
  }
  // Own keys only: the message is server text, and "toString" must not
  // resolve to Object.prototype's.
  if (Object.prototype.hasOwnProperty.call(TOKEN_REASONS, message)) {
    return { ok: false, reason: TOKEN_REASONS[message], message };
  }

  Sentry.captureException(new Error(`${rpc} failed: ${message}`), {
    tags: { feature: "versioning", stage: `${rpc}-rpc` },
    extra: { ...extra, code: error.code },
  });
  // An error with a SQLSTATE came from the database and rolled back; one
  // without never got a verdict back to us.
  return { ok: false, reason: "unknown", message, indeterminate: !error.code };
}

function badShape<T>(rpc: string, extra: Record<string, unknown>): VersionLineageOutcome<T> {
  Sentry.captureMessage(`${rpc} returned an unexpected shape`, {
    level: "error",
    tags: { feature: "versioning", stage: `${rpc}-shape` },
    extra,
  });
  return {
    ok: false,
    reason: "unknown",
    message: "Unexpected response from the server",
    indeterminate: true,
  };
}

function parseRename(value: unknown): { from: string; to: string } | null {
  return isRecord(value) && typeof value.from === "string" && typeof value.to === "string"
    ? { from: value.from, to: value.to }
    : null;
}

/** Validate and camel-case the jsonb `reorder_document_versions` returns. */
export function parseReorderVersionsResult(data: unknown): ReorderVersionsResult | null {
  if (!isRecord(data)) return null;
  const {
    version_group: versionGroup,
    head_document_id: headDocumentId,
    head_version: headVersion,
    head_relocated: headRelocated,
    head_renamed: headRenamed,
  } = data;
  if (typeof versionGroup !== "string") return null;
  if (typeof headDocumentId !== "number" || typeof headVersion !== "number") return null;
  return {
    versionGroup,
    headDocumentId,
    headVersion,
    headRelocated: headRelocated === true,
    headRenamed: parseRename(headRenamed),
  };
}

/** Validate and camel-case the jsonb `detach_document_version` returns. */
export function parseDetachVersionResult(data: unknown): DetachVersionResult | null {
  if (!isRecord(data)) return null;
  const {
    document_id: documentId,
    version_group: versionGroup,
    filename,
    renamed,
    source_version_group: sourceVersionGroup,
    source_head_renamed: sourceHeadRenamed,
    folder_id: folderId,
  } = data;
  if (typeof documentId !== "number" || typeof versionGroup !== "string") return null;
  if (typeof filename !== "string" || typeof sourceVersionGroup !== "string") return null;
  return {
    documentId,
    versionGroup,
    filename,
    renamed: parseRename(renamed),
    sourceVersionGroup,
    sourceHeadRenamed: parseRename(sourceHeadRenamed),
    folderId: typeof folderId === "number" ? folderId : folderId === null ? null : undefined,
  };
}

/**
 * Put a lineage's live versions in a new order. `documentIds` must be every
 * live version, oldest first — the last becomes the current version. A list
 * that is no longer the live set comes back as `versionsChanged`.
 */
export async function reorderDocumentVersions(params: {
  versionGroup: string;
  documentIds: number[];
}): Promise<VersionLineageOutcome<ReorderVersionsResult>> {
  const { data, error } = await supabase.rpc("reorder_document_versions", {
    p_version_group: params.versionGroup,
    p_document_ids: params.documentIds,
  });
  if (error) return failure("reorder_document_versions", error, params);
  const result = parseReorderVersionsResult(data);
  if (!result) return badShape("reorder_document_versions", params);
  return { ok: true, result };
}

/** Take one live version out of its lineage as a stand-alone document. */
export async function detachDocumentVersion(params: {
  documentId: number;
}): Promise<VersionLineageOutcome<DetachVersionResult>> {
  const { data, error } = await supabase.rpc("detach_document_version", {
    p_document_id: params.documentId,
  });
  if (error) return failure("detach_document_version", error, params);
  const result = parseDetachVersionResult(data);
  if (!result) return badShape("detach_document_version", params);
  return { ok: true, result };
}

/**
 * May the caller reorder this lineage? `document_lineage_editable` runs the
 * reorder RPC's own checks server-side, so it accounts for versions RLS hides
 * from the lineage read, which only the RPC's `forbidden` would otherwise
 * reveal, after the user had arranged the list. A missing RPC marks the
 * dialog's edits unavailable for the session; any other error throws.
 */
export async function fetchLineageEditable(versionGroup: string): Promise<boolean> {
  return fetchLineageProbe("document_lineage_editable", versionGroup);
}

/**
 * May the caller make a version of this lineage a separate file?
 * `document_lineage_detachable` adds detach's own requirement to the reorder
 * check: detach also renumbers the trashed versions it leaves behind, so those
 * must be changeable too. Errors as `fetchLineageEditable`.
 */
export async function fetchLineageDetachable(versionGroup: string): Promise<boolean> {
  return fetchLineageProbe("document_lineage_detachable", versionGroup);
}

async function fetchLineageProbe(
  rpc: "document_lineage_editable" | "document_lineage_detachable",
  versionGroup: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc(rpc, {
    p_version_group: versionGroup,
  });
  if (error) {
    // Deployed ahead of the migration: say so, as the write RPCs do, rather
    // than reading as a failed load.
    if (isMissingFunctionError(error)) {
      unavailable = true;
      return false;
    }
    throw new Error(error.message);
  }
  return data === true;
}

/** One row of a lineage as the Versioning dialog numbers it — trashed rows
 *  included, because they hold numbers too. */
export interface LineageRow {
  id: number;
  version: number | null;
  isDeleted: boolean;
  /** Where the row is filed; the RPCs check edit rights per row there. */
  projectId: number | null;
}

/**
 * Every row of a lineage, trashed ones included, so the dialog can show the
 * exact numbers a save will produce. `documents_select` does not filter
 * deleted rows (see `fetchLineageVersionStats`).
 */
export async function fetchLineageRows(versionGroup: string): Promise<LineageRow[]> {
  const { data, error, count } = await supabase
    .from("documents")
    .select("id, version, is_deleted, project_id", { count: "exact" })
    .eq("version_group", versionGroup);
  if (error) throw new Error(error.message);
  // PostgREST stops at max-rows with no error. Numbers projected from part of
  // a lineage would be wrong, so a short read is a failed read. The count is
  // taken after RLS, so this cannot see versions hidden from the caller; those
  // are what `fetchLineageEditable` answers for.
  if (typeof count === "number" && (data ?? []).length < count) {
    throw new Error(`lineage read truncated: ${(data ?? []).length} of ${count} rows`);
  }
  return (
    (data ?? []) as Array<{
      id: number;
      version: number | null;
      is_deleted: boolean | null;
      project_id: number | null;
    }>
  ).map((row) => ({
    id: row.id,
    version: row.version,
    isDeleted: row.is_deleted === true,
    projectId: row.project_id ?? null,
  }));
}
